import { useCallback } from 'react';
import { useSnackbar } from 'notistack';
import {
  CapaErrorCode,
  CapaPaymentMethodType,
  CapaQuotationChatRoomStatus,
  FetchPurchaseOrdersDocument,
  useCreatePaymentAfterCreatePurchaseOrderMutation,
  useFetchMyRefundBankAccountsQuery
} from '@generated/graphql';
import useDashboard from '@hooks/useDashboard';
import useIamport from '@hooks/useIamport';
import useQueryString from '@hooks/useQueryString';
import { useSelector } from '@store';
import errorMessage from '@utils/errorMessage';
import { storage } from '@utils/storage';
import { pgProviderToCapaEnumMap } from '../utils/map';
import { MobilePaymentRedirectQueryString } from '../types';
import { PayMethodEnum } from '../../../../../types/payment';
import { RequestPayResponse } from '../../../../../../@types/@iamport-type';

interface OnPayArgs {
  paymentMethod: CapaPaymentMethodType;
  totalAmount: number;
  projectName: string;
  merchant_uid: string;
}

interface OnCreatePaymentArgs {
  purchaseOrderId: string;
  paymentResult: RequestPayResponse;
  chatRoomStatus?: CapaQuotationChatRoomStatus;
}

interface OnRedirectAfterPaymentMobileArgs {
  chatRoomStatus?: CapaQuotationChatRoomStatus;
  isPaymentProcessing: React.MutableRefObject<boolean>;
}

interface UsePaymentArgs {
  partnerId: string;
  rfqId: string;
}

const usePayment = ({ partnerId, rfqId }: UsePaymentArgs) => {
  const {
    partnerId: tempPartnerId = '',
    quotationId: tempQuotationId = '',
    rfqId: tempRfqId = '',
    chatRoomId: tempCahtRoomId = ''
  } = storage.getItem.TEMP_SELECTED_QUOTATION_INFO ?? {};

  partnerId = partnerId || tempPartnerId;
  rfqId = rfqId || tempRfqId;

  const isDashboard = window.location.pathname.includes('/dashboard');
  const { requestPayment } = useIamport();
  const { enqueueSnackbar } = useSnackbar();
  const { onUpdateSelectedQuotation } = useDashboard();
  const { query } = useQueryString<
    MobilePaymentRedirectQueryString & { menu: string }
  >();
  const clientContact = useSelector(
    (state) => state.client.cellphoneNumber ?? ''
  );
  const quotationId = useSelector(
    (state) =>
      state.dashboard.selectedQuotationId?.quotationId ||
      (tempQuotationId ?? '')
  );

  const [
    createPaymentAfterCreatePurchaseOrder
  ] = useCreatePaymentAfterCreatePurchaseOrderMutation({
    refetchQueries: [
      {
        query: FetchPurchaseOrdersDocument,
        variables: {
          input: {
            rfqId,
            quotationPartnerId: partnerId
          }
        }
      }
    ],
    awaitRefetchQueries: true
  });

  const {
    data: refundAccountData,
    refetch
  } = useFetchMyRefundBankAccountsQuery({
    fetchPolicy: 'no-cache'
  });

  const refundAccountList = refundAccountData?.fetchMyRefundBankAccounts?.data;
  const refundAccount = refundAccountList?.[0];

  const initializedTempDatas = () => {
    storage.setItem.TEMP_CREATE_PURCAHSE_ORDER_ID();
    storage.setItem.TEMP_SELECTED_QUOTATION_INFO();
    storage.setItem.TEMP_REFUND_ACCOUNT_ID();
  };

  const setDashboardData = useCallback(() => {
    const tempQuotationInfo = storage.getItem.TEMP_SELECTED_QUOTATION_INFO;

    if (tempQuotationInfo) {
      if (quotationId && rfqId && partnerId) {
        onUpdateSelectedQuotation({
          ...tempQuotationInfo
        });
      }
    }
  }, [onUpdateSelectedQuotation, quotationId, rfqId, partnerId]);

  const removePaymentSearchParam = () => {
    window.history.pushState(
      '',
      '',
      `${window.location.pathname}?menu=create_purchaseorder&p=2`
    );
  };

  const onCreatePayment = useCallback(
    async ({
      paymentResult,
      purchaseOrderId,
      chatRoomStatus
    }: OnCreatePaymentArgs) => {
      const { pg_provider = 'nice', imp_uid } = paymentResult;
      const pgProvider = pgProviderToCapaEnumMap(pg_provider);
      const transactionId = imp_uid ?? '';

      const refundAccountId =
        storage.getItem.TEMP_REFUND_ACCOUNT_ID ?? refundAccount?.id ?? '';

      if (!paymentResult?.success) throw new Error(paymentResult?.error_msg);

      const response = await createPaymentAfterCreatePurchaseOrder({
        variables: {
          input: {
            partnerId,
            rfqId,
            purchaseOrderId,
            pgProvider,
            transactionId,
            refundAccountId,
            chatRoomStatus
          }
        }
      });

      const responsData = response.data?.createPaymentAfterCreatePurchaseOrder;
      const { result } = responsData ?? {};
      const isSuccess = result === CapaErrorCode.Success;

      if (!isSuccess) {
        throw new Error(
          '결제정보 저장에 실패했습니다. 결제내역은 자동으로 취소됩니다.'
        );
      } else {
        initializedTempDatas();
      }
    },
    [createPaymentAfterCreatePurchaseOrder, rfqId, partnerId, refundAccount]
  );

  const onPay = useCallback(
    async ({
      paymentMethod,
      totalAmount,
      merchant_uid,
      projectName
    }: OnPayArgs) => {
      const isCard = paymentMethod === CapaPaymentMethodType.CreditCard;
      const { data: refundAccountData } = await refetch();
      const refundAccountDataList =
        refundAccountData?.fetchMyRefundBankAccounts?.data;
      const refundAccount = refundAccountDataList?.[0];

      if (!isCard && !refundAccount) {
        throw new Error(
          '등록된 환불계좌를 찾을 수 없습니다. 환불계좌를 확인해주세요.'
        );
      }

      if (!requestPayment) {
        throw new Error('결제모듈 초기화에 실패했습니다. 다시 시도해주세요.');
      }

      const pay_method =
        paymentMethod === CapaPaymentMethodType.CreditCard
          ? PayMethodEnum.card
          : PayMethodEnum.vbank;

      const result = await requestPayment({
        pay_method,
        amount: totalAmount,
        merchant_uid,
        name: projectName,
        buyer_tel: clientContact
      });

      if (!result?.success) {
        throw new Error('결제에 실패했습니다. 다시 시도해주세요.');
      }

      return result;
    },
    [requestPayment, clientContact, refetch]
  );

  const onRedirectAfterPaymentMobile = useCallback(
    async ({
      chatRoomStatus,
      isPaymentProcessing
    }: OnRedirectAfterPaymentMobileArgs) => {
      const {
        imp_success = 'false',
        imp_uid,
        error_msg = '',
        error_code = '',
        merchant_uid = ''
      } = query;

      const purchaseOrderId = storage.getItem.TEMP_CREATE_PURCAHSE_ORDER_ID;
      const tempQuotationInfo = storage.getItem.TEMP_SELECTED_QUOTATION_INFO;
      const { quotationId } = tempQuotationInfo ?? {};

      /** '작성중 발주서 데이터'가 없거나 결제 후 redirect 접근이 아님 */
      if (!quotationId || !purchaseOrderId || !imp_uid) return;

      isPaymentProcessing.current = true;

      try {
        if (imp_success !== 'true')
          throw new Error(
            error_msg ?? '결제에 실패했습니다. 다시 시도해주세요.'
          );

        await onCreatePayment({
          purchaseOrderId,
          chatRoomStatus,
          paymentResult: {
            error_msg,
            imp_uid,
            success: true,
            error_code,
            merchant_uid
          }
        });

        return { result: CapaErrorCode.Success, chatRoomId: tempCahtRoomId };
      } catch (e) {
        const error = e as Error;

        if (isDashboard) {
          setDashboardData();
        }

        removePaymentSearchParam();

        enqueueSnackbar(error?.message ?? errorMessage.commonException, {
          variant: 'error'
        });
      }
    },
    [
      query,
      isDashboard,
      tempCahtRoomId,
      enqueueSnackbar,
      onCreatePayment,
      setDashboardData
    ]
  );

  return { onPay, onCreatePayment, onRedirectAfterPaymentMobile };
};

export default usePayment;
